import type { ColumnSpec, HostApi, PluginModule, Row, Table } from '@easydb/shared';
import { clearAppProgress, setAppProgress } from '../chrome/app-progress-signal.js';
import { hasRule, ruleNames, summarizeIssues } from '../table/validate-rules.js';
import { scanTable } from '../table/validate-scan.js';
import { clearRowErrors, ERROR_FIELD, errorColumnSpec, rowErrorsFrom, setRowErrors, type RowProblems } from '../table/row-errors.js';
import { focusTableWindow } from '../window-mgr/table-window-manager.js';
import { setTableLoading } from '../table/table-loading.js';
import { registerRunAction } from '../table/run-actions.js';
import { RunPickerDialog } from '../dialogs/run-picker-dialog.js';
import { requestVisibleRows } from '../table/visible-rows.js';

// **Run validations** — one item on the footer's ▶ Run menu — checks rows
// against their columns' rules (`notnull`, `max`, `unique` and a `validate`
// script) and hands what it finds back to the table's own grid.
//
// It had a ✓ button of its own until v0.0.476. The button is now shared with the
// script runner (`plugins/run-scripts.ts` owns it, `table/run-actions.ts` is the
// list it draws), and which columns and which rows to check are asked in one
// dialog rather than assumed to be all of both.
//
// A run leaves three things behind:
//
//  1. **A mark on every cell that is wrong**, pink like an empty one, with the
//     reason in its tooltip. This is what the user reads.
//  2. **The grid narrowed** to the rows with something wrong, so a big table shows
//     the work and nothing else.
//  3. **A `_error` column** holding each row's whole verdict as text — created
//     hidden, because (1) already says it in place. It is an ordinary column: the
//     columns editor shows it, and renaming it hands the messages over as data.
//
// This was a second TABLE of issues at first (`Pets issues`), on the grounds that
// filtering, sorting and exporting are things this app already does — for tables.
// It was the wrong place to work: a copy of a problem cannot be repaired, and it
// is stale the moment the real row is. See `table/row-errors.ts`.
//
// The scan is the only thing in the app that runs a column script over more than
// one row, so it gets a progress bar, it yields between pages, and Esc stops it.

export const meta: NonNullable<PluginModule['meta']> = {
  id: 'validate',
  name: 'Validate',
  type: 'ui',
  version: '0.2.0',
  description: 'Checks every row against its columns’ rules — required, maximum, unique, validation script — and shows what it finds in an _error column.',
  author: 'Marc Cawood',
  icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>',
  repo: 'https://github.com/cawoodm/easydbaccess/blob/main/packages/renderer/src/plugins/validate.ts',
};

/** Reported per column before the rest is counted rather than listed. */
const CAP_PER_COLUMN = 500;

/** Rows per `_error` write. Same reasoning as `materialize-script.ts`'s CHUNK. */
const MARK_CHUNK = 500;

export function init(_api: HostApi): void {
  registerRunAction({
    id: 'validate:run',
    label: 'Run validations',
    icon: 'check',
    order: 20,
    run: validateTable,
  });
}

/** The columns this run could check, in column order. */
export function ruleColumns(columns: readonly ColumnSpec[]): ColumnSpec[] {
  return columns.filter((c) => hasRule(c, true));
}

/**
 * Ask which columns and which rows, then scan and report.
 *
 * Both answers narrow what the run may CLEAR as well as what it checks — see
 * `spokeForEveryRow` below. A partial run adds messages and never takes one
 * back, because a rule it did not apply is not a rule that passed.
 */
async function validateTable(api: HostApi, table: Table): Promise<void> {
  const picker = RunPickerDialog.instance;
  const withRules = ruleColumns(table.columns);
  if (withRules.length === 0) {
    api.ui.dialogs.toast(`No column of "${table.name}" carries a rule, so there is nothing to check. Set Required, Maximum, Unique or a validation script in the columns editor.`, {
      kind: 'info',
      title: 'Validate',
    });
    return;
  }
  if (!picker) return;

  const coll = api.store.rows(table.id);
  const shown = requestVisibleRows(table.id)?.rows ?? null;
  const totalRows = coll.count ? await coll.count() : (await coll.find()).length;

  const answer = await picker.open({
    title: `Run validations — ${table.name}`,
    intro: 'Each ticked column is checked against its rules. Cells that are wrong are marked, the grid narrows to their rows, and the verdicts are written into the table’s _error column.',
    items: withRules.map((c) => ({ field: c.field, label: c.label || c.field, note: ruleNames(c, true).join(', ') })),
    visibleRows: shown?.length ?? totalRows,
    totalRows,
    runLabel: 'Check',
  });
  if (!answer) return;

  const columns = withRules.filter((c) => answer.fields.includes(c.field));
  if (columns.length === 0) return;
  const rows = answer.rows === 'visible' ? (shown ?? undefined) : undefined;
  await runValidation(api, table, columns, rows, columns.length === withRules.length && rows === undefined);
}

/** Scan the chosen columns over the chosen rows, then report. */
async function runValidation(api: HostApi, table: Table, columns: readonly ColumnSpec[], rows: readonly Row[] | undefined, whole: boolean): Promise<void> {
  const coll = api.store.rows(table.id);
  // Cleared BEFORE the scan, not after it. What is on screen while the scan runs
  // must not be the last run's verdict on rows the user has edited since — and the
  // grid takes the `_error` column and its filter down with it.
  clearRowErrors(table.id);
  let stop = false;
  // Esc cancels. The scan is the one operation here that can run for a minute, and
  // a key the label names is cheaper for everyone than a second progress widget.
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') stop = true;
  };
  document.addEventListener('keydown', onKey);

  try {
    const result = await scanTable(coll, columns, {
      runScripts: true,
      capPerColumn: CAP_PER_COLUMN,
      ...(table.labelColumn ? { labelField: table.labelColumn } : {}),
      ...(rows ? { rows } : {}),
      cancelled: () => stop,
      onProgress: (scanned, total) => {
        setAppProgress({
          label: `Checking ${table.name} — press Esc to stop`,
          ...(total > 0 ? { fraction: Math.min(1, scanned / total), detail: `${scanned.toLocaleString()} of ${total.toLocaleString()} rows` } : { detail: `${scanned.toLocaleString()} rows` }),
        });
        // And on the table's OWN window, the same bar an import draws. The
        // app-wide bar under the header is easy to miss when the thing you are
        // watching is a panel in the middle of the canvas — and a scan that runs
        // for a minute with nothing happening in the grid reads as a hang.
        setTableLoading(table.id, true, total > 0 ? Math.min(1, scanned / total) : undefined);
      },
    });

    if (result.noRules) {
      api.ui.dialogs.toast(`No column of "${table.name}" carries a rule, so there is nothing to check. Set Required, Maximum, Unique or a validation script in the columns editor.`, {
        kind: 'info',
        title: 'Validate',
      });
      return;
    }

    const errors = rowErrorsFrom(result.issues);
    // A run that stopped early, or that hit a per-column cap, did not speak for
    // every row: a row past the cap is still wrong and this run said nothing about
    // it. Such a run may add messages but must not take any back, or pressing Run
    // on a table with 600 broken rows would erase the verdict on 100 of them.
    //
    // `whole` is the same rule for the picker's two answers. A run over some of
    // the columns, or some of the rows, has said nothing about the rest — and a
    // message it never looked at is not a message it disproved.
    const spokeForEveryRow = whole && !result.cancelled && result.capped.size === 0;
    // The messages go into the table's own `_error` column BEFORE anything is
    // reported — including on a clean run, which has stale messages to clear.
    await writeMessages(api, table, errors, spokeForEveryRow ? result.stale : []);
    // Publishing marks the offending cells and narrows the grid to their rows. So
    // the rows are already waiting behind the dialog, which is why the choice
    // below only has to bring the window forward.
    setRowErrors(table.id, errors);

    if (result.issues.length === 0) {
      const how = result.cancelled ? `the first ${result.scanned.toLocaleString()} rows` : `all ${result.scanned.toLocaleString()} rows`;
      api.ui.dialogs.toast(`No issues in ${how} of "${table.name}".`, { kind: 'success', title: 'Validate' });
      return;
    }

    const lines = summarizeIssues(result.issues, result.capped, columns);
    // Both numbers, because they answer different questions: how much is wrong, and
    // how much of the table it is wrong in.
    const head = `${result.issues.length.toLocaleString()} issue${result.issues.length === 1 ? '' : 's'} in ${errors.size.toLocaleString()} of ${result.scanned.toLocaleString()} rows of "${table.name}"${
      result.cancelled ? ', before you stopped it' : ''
    }.`;
    const pick = await api.ui.dialogs.choice(
      `${head}\n\n${lines.join('\n')}\n\n"${table.name}" now shows those rows only. Each cell that is wrong is marked, with the reason in its tooltip. Fix them, then run the check again.`,
      ['Show me', 'Close'],
      'Validate',
    );
    if (pick === 'Show me') focusTableWindow(table.id);
  } finally {
    document.removeEventListener('keydown', onKey);
    clearAppProgress();
    setTableLoading(table.id, false);
  }
}

/**
 * Write each row's verdict into the table's own `_error` column, and clear the
 * ones a previous run left on rows that are clean now.
 *
 * Why write at all, when the grid could hold the messages in memory: because a
 * column the columns editor can show has to BE a column, and a column whose cells
 * are empty in the store is a trap — it exports as blank, and renaming it (which
 * is how a user keeps a copy — a rename re-keys every row) would hand over
 * nothing. So `_error` is ordinary data that Validate owns and rewrites.
 *
 * Bounded by the rows involved, never the table: the flagged rows plus the ones
 * carrying a message that is no longer true. A clean 600,000-row table is not
 * written to at all.
 */
async function writeMessages(api: HostApi, table: Table, errors: ReadonlyMap<string, RowProblems>, stale: readonly string[]): Promise<void> {
  // A source-backed table's rows are derived or remote — a projection computes
  // them, Datasette owns them — and one the user marked read-only must be left
  // alone. The cell marks and the tooltips still work there; only the column does
  // not, because there is nowhere to put it.
  if (table.source || table.readonly) return;
  const coll = api.store.rows(table.id);

  // Re-read, rather than trusting the record the scan started from: a scan of a big
  // table runs for a minute, and a columns editor saved during it would be undone
  // by patching the older array back.
  const fresh = (await api.store.tables.findOne(table.id)) ?? table;
  if (errors.size > 0 && !fresh.columns.some((c) => c.field === ERROR_FIELD)) {
    // Created hidden, once. A later run must not re-hide a column the user chose
    // to unhide, so an existing one is never patched — and a RENAMED one is not
    // found here at all, which is what makes it theirs and this a fresh column.
    await api.store.tables.patch(table.id, { columns: [...fresh.columns, errorColumnSpec()], updatedAt: Date.now() });
  }

  const wanted = new Map<string, string>([...[...errors].map(([id, p]) => [id, p.message] as const), ...stale.map((id) => [id, ''] as const)]);
  if (wanted.size === 0) return;

  // ONE read and batched writes.
  //
  // This was `findOne` + `patch` per row, which is two round trips, two
  // transactions and two grid-waking broadcasts for every flagged row — the same
  // arithmetic that made running a column script over 4 000 rows take two
  // minutes. A table with a few thousand problems spent longer being MARKED than
  // being checked. See `table/materialize-script.ts`, which fixed the twin.
  const byId = new Map((await coll.find()).map((r) => [r.id, r]));
  const pending: Row[] = [];
  for (const [rowId, text] of wanted) {
    const row = byId.get(rowId);
    // Deleted since the scan read it. The problem went with it.
    if (!row) continue;
    // Unchanged rows are left alone: a write would bump `updatedAt` and give
    // sync a row to carry for no reason.
    if (String(row.data[ERROR_FIELD] ?? '') === text) continue;
    pending.push({ ...row, data: { ...row.data, [ERROR_FIELD]: text }, updatedAt: Date.now() });
  }

  const bulk = coll.bulkUpdate?.bind(coll);
  for (let from = 0; from < pending.length; from += MARK_CHUNK) {
    const batch = pending.slice(from, from + MARK_CHUNK);
    if (bulk) await bulk(batch);
    else for (const row of batch) await coll.patch(row.id, { data: row.data, updatedAt: row.updatedAt });
    if (pending.length > MARK_CHUNK) {
      const done = Math.min(from + batch.length, pending.length);
      setAppProgress({ label: `Marking rows in ${table.name}`, fraction: done / pending.length, detail: `${done.toLocaleString()} of ${pending.length.toLocaleString()}` });
      setTableLoading(table.id, true, done / pending.length);
    }
  }
}
